import { z } from 'zod';
import { BAUJAHR_MIN, ZUSTAND_STUFEN, baujahrMax, pruefeObjekt, valuationObjectBaseSchema, valuationObjectSchema, valuationLocationSchema, } from './schemas/valuation';
import { maskenFelder } from './iazi-maske';
/**
 * Provider-agnostic AVM (Automated Valuation Model) contracts. M5 ships against
 * deterministic STUBS so the Bewertung funnel + map work end-to-end today; the
 * real commercial provider (IAZI primary, PriceHubble fallback) slots in behind
 * one env flag later ("flip the switch"). One provider feeds BOTH the
 * per-object Bewertung AND the regional CHF/m² map layer.
 *
 * Money is always CHF. The `AvmProvider` interface + its DI token live
 * backend-side (apps/backend/src/avm) — the browser only ever needs the result
 * shape, which is the shared {@link valuationEstimateSchema}. Provider API keys
 * are server-side ONLY and must never reach the edge/client.
 */
export const AVM_CURRENCY = 'CHF';
/**
 * IAZI product tier. `offered` = the fast public estimate (CHOfferedPrice),
 * Gemeinde-only, feeds the public Bewertung funnel. `paid` = the exact internal
 * estimate (CHPaidPrice), used by the CRM tool under the owner's data-processing
 * consent; it MAY carry the precise street address. Defaults to `offered`.
 */
export const avmProductSchema = z.enum(['offered', 'paid']);
/**
 * Exact street address — ONLY for the internal `paid` product (CRM, under owner
 * consent). It is deliberately NOT part of {@link valuationLocationSchema} so it
 * can never leak into the public, PII-minimised Gemeinde-only funnel/event.
 */
export const avmAddressSchema = z.object({
    street: z.string().trim().min(1).max(200),
    town: z.string().trim().max(120).optional(),
});
/**
 * Order B2 (owner 31-8) — internal ppch-only details, the field-parity follow-up
 * of the #505 contract audit (docs/IAZI-CONTRACT-AUDIT.md par.3, CRM list).
 * DELIBERATELY NOT part of `objektMerkmaleSchema`: that object rides the
 * PUBLIC funnel wire; these fields exist only on the internal `paid` product
 * (CRM Bewertungsformular). All optional — an empty field means "unknown" and
 * is NOT sent (IAZI substitutes and reports via `replaceCode`); we never send
 * invented defaults.
 *
 * IAZI mapping (avm.iazi.ts, per served-spec fixture):
 *   ausbaustandard -> buildQuality 1..4 (Einfach 4 … Luxus 1; without it the
 *     Zustand rating keeps feeding BOTH fields, as since the 28-8 audit)
 *   renovationsjahr -> renovationYear (a BARE year — measured 16-9)
 *   B3-2 (16-9), the thirteen remaining A2InputPPCHV2 fields — per model:
 *   lageImOrt -> qualityMicro (paid a1/a2/a3; WITHOUT it no qualityMicro at
 *     all — IAZI 27-9: no default for the Mikrolage; only offered sends 3) ·
 *   badezimmer -> bathNb · separateWc -> sepWcNb · garagenparkplaetze ->
 *   parkCoverNb · tiefgaragenplaetze -> parkOpenNb · aussenparkplaetze ->
 *   parkExternalNb · ferienobjekt -> primSecFlag · minergie -> minergieCode
 *   0..6 · servitute -> servituteFlag (all paid models a1/a2/a3) ·
 *   dachstockAusbaubar -> roofFlag (EFH a2 ONLY) · baurechtVerfall ->
 *   buildRightDate YYYYMMDD (all; 0 = no building right, B3-1) ·
 *   zaehlweiseZimmer -> roomShiftCode 0/1/2 (all; B3-1 default 0) ·
 *   luxusobjekt -> luxusFlag 0/0.5/1 (a2/a3; an explicit value beats the flag
 *   derived from Ausbaustandard «Luxus»)
 *   volumen+volumenNorm -> volume + volumeCode (EFH a2 / MFH a1 — the EWG a3
 *     input has no volume fields)
 *   etage -> floor (EWG a3 only) · geschosse -> floorNb · wohnungen -> flatNb
 *     (a1+a3; the EFH a2 knows neither) · balkonflaeche -> surfaceBalcony (a3)
 *   jahresmiete -> rentAnnual (MFH a1 only; CHF/Jahr, integer)
 *   Order MFH-Rendite (23-9), MFH a1 only — the Web App's Renditeliegenschaft:
 *   wohnungsspiegel -> flat10Nb … flat70Nb + flatLoftNb (a USED mix, Σ ≥ 1,
 *     rides as all fourteen, an empty box as 0 — the Web App shows fourteen
 *     «0»; flatNb = Σ) · mietanteilGeschaeft -> rentRepartitionPercent ·
 *     bueroflaeche/gewerbeflaeche/verkaufsflaeche/unterrichtsflaeche/
 *     medizinflaeche -> surfaceOffice/Commercial/Retail/Education/Medical
 *     (HNF2…HNF6) · aufzuege -> liftNb (a COUNT; beats the Lift boolean)
 */
export const VOLUMEN_NORMEN = ['gvz', 'sia116', 'andere', 'sia416'];
export const VOLUMEN_NORM_LABEL = {
    gvz: 'GVZ (Gebäudeversicherung)',
    sia116: 'SIA 116',
    andere: 'Andere Norm',
    sia416: 'SIA 416',
};
/** Ausbaustandard (quality words) → IAZI buildQuality 4 · 3 · 2 · 1 (avm.iazi.ts). */
export const AUSBAUSTANDARD_STUFEN = ['einfach', 'normal', 'gehoben', 'luxus'];
/**
 * German labels of the four quality levels — their OWN table (#576 MINOR-2):
 * until then the CRM rendered the Ausbaustandard through the Zustand label
 * table, which only worked because that table still carries the four legacy
 * words. Clearing the legacy must not silently unlabel this column.
 */
export const AUSBAUSTANDARD_LABEL = {
    einfach: 'Einfach',
    normal: 'Normal',
    gehoben: 'Gehoben',
    luxus: 'Luxus',
};
export const istAusbaustandard = (v) => typeof v === 'string' && AUSBAUSTANDARD_STUFEN.includes(v);
/** Display label; an unknown stored value is shown as it is — never invented. */
export const ausbaustandardLabel = (v) => istAusbaustandard(v) ? AUSBAUSTANDARD_LABEL[v] : (v ?? '');
/** IAZI `qualityCode` (spec) as the Web Tool words it — B3-3 write-back, B3-5 display. */
export const IAZI_QUALITAET_LABEL = {
    0: 'Keine Schätzung',
    1: 'Extrapolierte Schätzung',
    2: 'Schätzqualität mittel',
    3: 'Schätzqualität gut',
    4: 'Schätzung mit ersetzten Daten',
};
export const iaziQualitaetLabel = (code) => typeof code === 'number' ? (IAZI_QUALITAET_LABEL[code] ?? `Code ${code}`) : '';
// ── Order B3-5 (16-9): what the quality verdict MEANS for the display ────────
/** «indikativ, mit Standardannahmen»: an extrapolated estimate (1) or one on substituted data (4). */
export const istIndikativ = (code) => code === 1 || code === 4;
export const INDIKATIV_LABEL = 'indikativ, mit Standardannahmen';
/**
 * «fair»/«good» (2/3): the only verdicts WITHOUT the label. 0 (none), 1
 * (extrapolated), 4 (substituted data) and «no code» (stub, rows before B3-3)
 * carry «indikativ, mit Standardannahmen» next to the figures of the
 * Bewertungsbericht (`schaetzung.indikativ`), and the internal one-pager leads
 * with the band for them.
 *
 * Owner 30-9: it NO LONGER decides whether the point value travels. The
 * customer's report (PDF and Bericht-Ansicht, behind the confirmed link)
 * always carries the Schätzwert (`bericht-daten.ts`); the E8 mail and the
 * funnel's result screen show the band only, for EVERY code — their own rule
 * (the click on «Bericht öffnen» confirms the address), not this function's.
 */
export const zeigtBetragOeffentlich = (code) => code === 2 || code === 3;
/**
 * B3-5b: the customer-facing note for an indicative verdict — shown under the
 * band on the result screen and in the E8 mail. Only 1 (extrapolated) and 4
 * (substituted data) carry one; 2/3 and «no code» (stub) show nothing.
 */
export const IAZI_QUALITAET_HINWEIS_OEFFENTLICH = {
    1: 'Hinweis: Diese Einschätzung wurde ausserhalb des üblichen Wertebereichs des Modells extrapoliert – bitte prüfen Sie Ihre Angaben.',
    4: 'Hinweis: Diese Einschätzung beruht teilweise auf Standardannahmen – fehlende Angaben wurden vom Bewertungsmodell ergänzt.',
};
export const oeffentlicherQualitaetsHinweis = (code) => typeof code === 'number' ? IAZI_QUALITAET_HINWEIS_OEFFENTLICH[code] : undefined;
/**
 * German labels of the IAZI model INPUT names (`replaceCode`, spec fixture
 * `iazi-v4-inputs.json`) — the wording of the Bewertungsformular. An unknown
 * name renders as itself (never dropped: the adapter allowlists the names).
 */
export const IAZI_FELD_LABEL = {
    bathNb: 'Badezimmer',
    buildCondition: 'Zustand',
    buildQuality: 'Ausbaustandard',
    buildRightDate: 'Baurecht (Ablaufdatum)',
    buildYear: 'Baujahr',
    flatCondition: 'Zustand der Wohnung',
    flatNb: 'Anzahl Wohnungen',
    // Order MFH-Rendite (23-9): the Wohnungsspiegel as the Web App words it.
    flat10Nb: '1-Zimmer-Wohnungen',
    flat15Nb: '1½-Zimmer-Wohnungen',
    flat20Nb: '2-Zimmer-Wohnungen',
    flat25Nb: '2½-Zimmer-Wohnungen',
    flat30Nb: '3-Zimmer-Wohnungen',
    flat35Nb: '3½-Zimmer-Wohnungen',
    flat40Nb: '4-Zimmer-Wohnungen',
    flat45Nb: '4½-Zimmer-Wohnungen',
    flat50Nb: '5-Zimmer-Wohnungen',
    flat55Nb: '5½-Zimmer-Wohnungen',
    flat60Nb: '6-Zimmer-Wohnungen',
    flat65Nb: '6½-Zimmer-Wohnungen',
    flat70Nb: '≥ 7-Zimmer-Wohnungen',
    flatLoftNb: 'Loft-Wohnungen',
    floor: 'Stockwerk',
    floorNb: 'Geschosse',
    // The spec field is a COUNT («Number of lifts»); the Web App asks «Anzahl Aufzüge».
    liftNb: 'Anzahl Aufzüge',
    luxusFlag: 'Luxusobjekt',
    maisonetteFlag: 'Maisonette',
    minergieCode: 'Minergie',
    // #587 MINOR-2: the «ersetzt» hint uses the same words as the input field.
    ownershipPercent: 'Wertquote (‰)',
    parkCoverNb: 'Separate Garagen',
    parkExternalNb: 'Abstellplätze im Freien',
    parkOpenNb: 'Tiefgaragenplätze',
    primSecFlag: 'Ferienobjekt',
    qualityFlatHouse: 'Lage im Gebäude',
    qualityMicro: 'Lage im Ort',
    qualityMicroSourceCode: 'Quelle der Lage im Ort',
    renovationYear: 'Letzte Renovation',
    // B11 (IAZI 29-9, answer 1): `rentAnnual` IS the «Mietwert nachhaltig» — the
    // effective rent is only information (was «Mietertrag effektiv» since A5).
    rentAnnual: 'Mietwert nachhaltig',
    rentRepartitionPercent: 'Mietanteil Geschäft (%)',
    roofFlag: 'Dachstock ausbaubar',
    roomNb: 'Zimmer',
    roomShiftCode: 'Zählweise Zimmer',
    sepWcNb: 'Separate WC',
    servituteFlag: 'Wertrelevante Servitute',
    surfaceBalcony: 'Balkon-/Terrassenfläche',
    surfaceCommercial: 'Gewerbefläche (HNF3)',
    surfaceEducation: 'Unterrichtsfläche (HNF5)',
    surfaceGround: 'Grundstückfläche',
    surfaceLiving: 'Wohnfläche',
    surfaceMedical: 'Medizinische Fläche (HNF6)',
    surfaceOffice: 'Bürofläche (HNF2)',
    surfaceRetail: 'Verkaufs-/Lagerfläche (HNF4)',
    typeFlatBuildingCode: 'Gebäudetyp',
    typeFlatCode: 'Wohnungstyp',
    typeHouseCode: 'Haustyp',
    volume: 'Gebäudevolumen',
    volumeCode: 'Volumen-Norm',
};
export const iaziFeldLabel = (name) => IAZI_FELD_LABEL[name] ?? name;
/**
 * The plausibility CHECK names IAZI sends in `checkingCode` (measured 16-9,
 * run 4, ppch/a2) in the Web Tool's wording. An unknown check renders as its
 * raw name — never dropped, never invented. The adapter re-exports this table
 * as `IAZI_CHECK_TEXTS` (B3-1 log line).
 */
export const IAZI_PRUEFUNG_TEXT = {
    QualityRoomNb: 'Ungewöhnliche Anzahl Zimmer',
    QualitySurfaceLiving: 'Ungewöhnliche Wohnfläche',
    QualityRatioRoomNbSurfaceLiving: 'Ungewöhnliches Verhältnis Wohnfläche / Anzahl Zimmer',
    QualityVolume: 'Ungewöhnliches Raumvolumen',
    QualityParkExternal: 'Ungewöhnliche Anzahl Abstellplätze',
    QualityMaxParkCoverParkOpen: 'Ungewöhnliche Anzahl Garagenplätze',
};
export const iaziPruefungText = (check) => IAZI_PRUEFUNG_TEXT[check] ?? check;
/**
 * RV-B11 MINOR-3: the upper bound of OUR volume approximation (Nettofläche × 5)
 * in m³. Neither the served spec nor its trimmed fixture sets a bound on
 * `volume` (0× minimum/maximum, measured 29-9); the only bound we know is
 * IAZI's own live validator of 16-9 («Raumvolumen nicht zwischen 1 und 10000»,
 * ppch/a2). Above it the approximation goes AT the bound, marked
 * `volume_begrenzt`; the adapter holds the lower bound 1 m³ (`avm.iazi.ts`).
 */
export const IAZI_VOLUMEN_NAEHERUNG_MAX = 10_000;
/** 10000 → «10'000» (the Swiss thousands separator, deterministic — no locale). */
const tausender = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
/**
 * B11 (IAZI 29-9, answer 9): what OUR approximation of an input is called on
 * the CRM page and in the PDF — «Volumen: Näherung (Nettofläche × 5)». One
 * table, so the Bewertung detail and the Bericht (B9) say the same words.
 */
export const IAZI_NAEHERUNG_LABEL = {
    volume: 'Volumen: Näherung (Nettofläche × 5)',
    volume_begrenzt: `Volumen: auf ${tausender(IAZI_VOLUMEN_NAEHERUNG_MAX)} m³ begrenzt`,
};
// ── Order B3-2 (16-9): the vocabularies of the thirteen remaining paid fields ─
/** Lage im Ort — IAZI `qualityMicro` 1 Ausserordentlich … 5 Schlecht, half steps (Web Tool). */
export const LAGE_IM_ORT_STUFEN = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
export const LAGE_IM_ORT_LABEL = {
    '1': 'Ausserordentlich',
    '1.5': 'Ausserordentlich – Sehr gut',
    '2': 'Sehr gut',
    '2.5': 'Sehr gut – Gut',
    '3': 'Gut',
    '3.5': 'Gut – Mittel',
    '4': 'Mittel',
    '4.5': 'Mittel – Schlecht',
    '5': 'Schlecht',
};
export const lageImOrtLabel = (v) => typeof v === 'number' ? (LAGE_IM_ORT_LABEL[String(v)] ?? String(v)) : '';
/** Lage im Gebäude — IAZI `qualityFlatHouse` 1 Sehr gut … 4 Schlecht, half steps (Web Tool, measured 18-9: Gut = 2). Micro-6. */
export const LAGE_IM_GEBAEUDE_STUFEN = [1, 1.5, 2, 2.5, 3, 3.5, 4];
export const LAGE_IM_GEBAEUDE_LABEL = {
    '1': 'Sehr gut',
    '1.5': 'Sehr gut – Gut',
    '2': 'Gut',
    '2.5': 'Gut – Mittel',
    '3': 'Mittel',
    '3.5': 'Mittel – Schlecht',
    '4': 'Schlecht',
};
export const lageImGebaeudeLabel = (v) => typeof v === 'number' ? (LAGE_IM_GEBAEUDE_LABEL[String(v)] ?? String(v)) : '';
/** Minergie — spec `minergieCode` 0 no … 6 (the fixture truncates the last name; code 6 = Minergie-A-Eco). */
export const MINERGIE_STUFEN = [
    'keine',
    'minergie',
    'minergie_p',
    'minergie_eco',
    'minergie_p_eco',
    'minergie_a',
    'minergie_a_eco',
];
export const MINERGIE_LABEL = {
    keine: 'Kein Minergie',
    minergie: 'Minergie',
    minergie_p: 'Minergie-P',
    minergie_eco: 'Minergie-Eco',
    minergie_p_eco: 'Minergie-P-Eco',
    minergie_a: 'Minergie-A',
    minergie_a_eco: 'Minergie-A-Eco',
};
/** Zählweise Zimmer — spec `roomShiftCode` 0 kitchen not counted · 1 half · 2 one. */
export const ZAEHLWEISEN = ['kueche_nicht', 'kueche_halb', 'kueche_ganz'];
export const ZAEHLWEISE_LABEL = {
    kueche_nicht: 'Küche nicht gezählt',
    kueche_halb: 'Küche als ½ Zimmer gezählt',
    kueche_ganz: 'Küche als 1 Zimmer gezählt',
};
/** Luxusobjekt — spec `luxusFlag` 0 no · 0.5 partial · 1 yes. */
export const LUXUS_STUFEN = ['nein', 'teilweise', 'ja'];
export const LUXUS_LABEL = {
    nein: 'Nein',
    teilweise: 'Teilweise',
    ja: 'Ja',
};
/** Baurecht-Verfalldatum as ISO `YYYY-MM-DD` (the adapter sends YYYYMMDD). */
export const BAURECHT_DATUM = /^\d{4}-\d{2}-\d{2}$/;
const istKalenderdatum = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso;
};
// ── Order MFH-Rendite (23-9): the Wohnungsspiegel of a Renditeliegenschaft ──
/**
 * The fourteen boxes of the IAZI Web App's Wohnungsspiegel, in its order:
 * 1 · 1½ · … · 6½ · ≥ 7 Zimmer, then Loft (spec `flat10Nb … flat70Nb`,
 * `flatLoftNb`; the adapter holds the name map). Measured 23-9: the Web App
 * shows all fourteen as «0» and «Anzahl Wohnungen» as their sum, Loft included.
 */
export const WOHNUNGSGROESSEN = [
    'zi10',
    'zi15',
    'zi20',
    'zi25',
    'zi30',
    'zi35',
    'zi40',
    'zi45',
    'zi50',
    'zi55',
    'zi60',
    'zi65',
    'zi70',
    'loft',
];
export const WOHNUNGSGROESSE_LABEL = {
    zi10: '1-Zimmer',
    zi15: '1½-Zimmer',
    zi20: '2-Zimmer',
    zi25: '2½-Zimmer',
    zi30: '3-Zimmer',
    zi35: '3½-Zimmer',
    zi40: '4-Zimmer',
    zi45: '4½-Zimmer',
    zi50: '5-Zimmer',
    zi55: '5½-Zimmer',
    zi60: '6-Zimmer',
    zi65: '6½-Zimmer',
    zi70: '≥ 7-Zimmer',
    loft: 'Loft',
};
/** Zimmer per Wohnung — «≥ 7» counts as 7 (order A6); a Loft has no room count. */
export const WOHNUNGSGROESSE_ZIMMER = {
    zi10: 1,
    zi15: 1.5,
    zi20: 2,
    zi25: 2.5,
    zi30: 3,
    zi35: 3.5,
    zi40: 4,
    zi45: 4.5,
    zi50: 5,
    zi55: 5.5,
    zi60: 6,
    zi65: 6.5,
    zi70: 7,
};
/** Anzahl Wohnungen of a mix — the Loft counts (Web App: 2 × 3-Z + 1 Loft ⇒ 3). */
export const wohnungsspiegelSumme = (ws) => WOHNUNGSGROESSEN.reduce((summe, g) => summe + (ws?.[g] ?? 0), 0);
/**
 * «Zimmer total» suggested from the mix (order A6): Σ(count × rooms), ≥ 7 as 7.
 * `undefined` when there is nothing to suggest: an empty mix, or a Loft in it —
 * a Loft has no room count, so any total would be invented.
 */
export const wohnungsspiegelZimmer = (ws) => {
    if (wohnungsspiegelSumme(ws) === 0 || (ws?.loft ?? 0) > 0)
        return undefined;
    let zimmer = 0;
    for (const [g, pro] of Object.entries(WOHNUNGSGROESSE_ZIMMER)) {
        zimmer += (ws?.[g] ?? 0) * pro;
    }
    return zimmer;
};
/**
 * The mix as one line — «3 × 3½-Zimmer · 4 × 4½-Zimmer (7 Wohnungen)» — for the
 * Bewertung detail page AND the approval card of a customer proposal (RV612
 * nit: the card showed `[object Object]`). One source; «1 Wohnung» singular.
 */
export const wohnungsspiegelText = (v) => {
    const ws = (v !== null && typeof v === 'object' ? v : {});
    const teile = WOHNUNGSGROESSEN.filter((g) => (ws[g] ?? 0) > 0).map((g) => `${ws[g]} × ${WOHNUNGSGROESSE_LABEL[g]}`);
    const summe = wohnungsspiegelSumme(ws);
    if (summe === 0)
        return '—';
    return `${teile.join(' · ')} (${summe} ${summe === 1 ? 'Wohnung' : 'Wohnungen'})`;
};
const wohnungsAnzahl = z.number().int().min(0).max(1000).optional();
/**
 * 0 is allowed per box (the Web App's own default), but a mix that is THERE
 * must hold a flat (Σ ≥ 1): an all-zero mix is an unused one, and «unused»
 * has one form — absent (master 23-9 09:38).
 */
export const wohnungsspiegelSchema = z
    .object(Object.fromEntries(WOHNUNGSGROESSEN.map((g) => [g, wohnungsAnzahl])))
    .strict()
    .superRefine((ws, ctx) => {
    if (wohnungsspiegelSumme(ws) < 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Wohnungsspiegel ohne Wohnung — mindestens ein Feld über 0 angeben oder leer lassen',
        });
    }
});
/** A Nutzfläche (HNF2…HNF6) in m² — 0 is a measured «keine», as with the Balkon. */
const nutzflaeche = z.number().nonnegative().max(1_000_000).optional();
/**
 * At most ONE decimal (master 23-9 10:16): the Web App shows «Mietanteil
 * Geschäft» with one decimal («30.0») and the Wohnen/Gemischt line lies between
 * 15 and 15.1 — half steps would make the subcategory depend on rounding.
 * Checked with a TOLERANCE, not `multipleOf(0.1)`: in floating point 0.3 is no
 * multiple of 0.1. The form uses the same check.
 */
export const hoechstensEineDezimale = (v) => Math.abs(v * 10 - Math.round(v * 10)) < 1e-6;
/**
 * N3 (IAZI 30-9): «Keine Renovation». The served spec, `renovationYear` on A1,
 * A2 and A3 (backend `fixtures/iazi-v4-inputs.json`): «In case of no
 * renovation, 0 should be passed.» 0 is an ANSWER about the building — the
 * Makler's «Keine Renovation» — and never a default: an absent year stays
 * «unbekannt» and is not sent (the a2/a3 fill it in, the a1 refuses it — the
 * Rendite form therefore asks, and the server holds a paid MFH call without it).
 */
export const RENOVATION_KEINE = 0;
/** The checkbox of the Bewertungsformular. */
export const RENOVATION_KEINE_LABEL = 'Keine Renovation';
/** How a stored 0 READS (CRM detail, Bewertungsbericht) — the word, never «0». */
export const RENOVATION_KEINE_WERT = 'keine';
/**
 * A year in or before the Baujahr: IAZI's validator refuses it («Letzte
 * Sanierung nicht zwischen Baujahr und …», model error 3 — prod 30-9 12:32,
 * Stockweg 6: 2024 = 2024). A Neubau answers Sanierung «Nein» (review 8-10 F7:
 * the form's word since IAZI-order 8-10, not the old checkbox).
 */
export const RENOVATIONSJAHR_NACH_BAUJAHR = 'Sanierungsjahr muss nach dem Baujahr liegen (Neubau: Sanierung ‹Nein› wählen)';
/** The a1 refuses a missing year (owner 30-9 12:43, hard refresh) — the Rendite form asks. */
export const RENOVATIONSJAHR_RENDITE_PFLICHT = 'Bei Renditeliegenschaften: Sanierungsjahr angeben oder Sanierung ‹Nein› wählen';
/** IAZI-order 8-10 A1: the free texts of the mask — bounded, our choice. */
export const GRUNDBUCH_MAX = 60;
export const BEMERKUNGEN_MAX = 500;
export const paidMerkmaleSchema = z
    .object({
    /**
     * @deprecated legacy (IAZI-order 8-10 A1): the four quality words of rows
     * before 8.10. — read through the bridge 4→1 (`CONDITION_RATING`), never
     * offered again; the mask asks {@link paidMerkmaleSchema} `bauqualitaet`.
     */
    ausbaustandard: z.enum(AUSBAUSTANDARD_STUFEN).optional(),
    /** Bauqualität — HEDO's seven steps 1 (Sehr gut) … 4 (Schlecht), half steps (IAZI `buildQuality`). */
    bauqualitaet: z.number().min(1).max(4).multipleOf(0.5).optional(),
    /** MFH: Mietertrag effektiv CHF p.a. — information only, never sent (IAZI 29-9). */
    mietertragEffektiv: z.number().int().min(0).max(100_000_000).optional(),
    /** Nutzungsart (HEDO «Selbstgenutzt / Fremdvermietet») — no API field, information only. */
    nutzungsart: z.enum(['selbstgenutzt', 'fremdvermietet']).optional(),
    /** Grundbuch (IAZI `landRegister`). */
    grundbuch: z.string().trim().max(GRUNDBUCH_MAX).optional(),
    /** Grundbuch-Nr. (IAZI `landRegisterNumber`). */
    grundbuchNr: z.string().trim().max(GRUNDBUCH_MAX).optional(),
    /** Baurecht Nein/Ja — «Nein» and «leer» differ (HEDO Pflicht); with Ja the date `baurechtVerfall`. */
    baurecht: z.boolean().optional(),
    /** Bemerkungen (IAZI `info`) — free text to IAZI; no personal data (form hint). */
    bemerkungen: z.string().trim().max(BEMERKUNGEN_MAX).optional(),
    /**
     * Jahr der letzten Renovation; leer = unbekannt (Feld wird nicht gesendet).
     * N3: {@link RENOVATION_KEINE} (0) = «Keine Renovation» (IAZI-Spec) — sonst
     * ein Jahr ab {@link BAUJAHR_MIN}; 1 … 1499 bleibt ungültig.
     */
    renovationsjahr: z
        .number()
        .int()
        .max(2100)
        .refine((j) => j === RENOVATION_KEINE || j >= BAUJAHR_MIN, {
        message: `renovationsjahr: 0 (keine Renovation) oder ein Jahr ab ${BAUJAHR_MIN}`,
    })
        .optional(),
    /** Gebäudevolumen in m3 (inkl. Garagen) — GVZ-Police/SIA. */
    volumen: z.number().positive().max(10_000_000).optional(),
    /** Norm des Volumens; Pflicht sobald `volumen` gesetzt ist (und umgekehrt). */
    volumenNorm: z.enum(VOLUMEN_NORMEN).optional(),
    /**
     * EWG: Stockwerk der Wohnung (0 = EG).
     *
     * MINOR-7 (hermeting #519): géén UG in deze keten. De handinvoer in het
     * CRM kent geen minteken (`ZahlField ganzzahl`), het register-pad laat
     * negatieve `stockwerkEtage`-waarden bewust weg (create.tsx,
     * `uebernimmWohnung`) en de IAZI-semantiek van een negatieve `floor` is
     * onbevestigd (abname). Een ondergrens van −10 was daarmee een belofte
     * zonder producent; het schema zegt nu wat de keten doet: 0 = EG is de
     * bodem. Wordt UG ooit gewenst, dan zijn dít de drie plekken.
     */
    etage: z.number().int().min(0).max(120).optional(),
    /** Anzahl Geschosse des Gebäudes (GWR `GASTW`). */
    geschosse: z.number().int().min(1).max(200).optional(),
    /** Anzahl Wohnungen im Gebäude (GWR `GANZWHG`). */
    wohnungen: z.number().int().min(1).max(1000).optional(),
    /** EWG: Balkon-/Terrassenfläche in m2 — 0 = kein Balkon (master 16-9: das
     *  Formular klemmte 0 auf 1 und sendete `surfaceBalcony 1`). */
    balkonflaeche: z.number().nonnegative().max(10_000).optional(),
    /**
     * MFH: der nachhaltige Mietwert in CHF pro Jahr, netto ohne Nebenkosten,
     * inkl. Parkplätze (ganzzahlig — IAZI `rentAnnual` int32; IAZI 29-9: der
     * effektive Mietertrag ist nur Information). Der Feldname bleibt.
     */
    jahresmiete: z.number().int().positive().max(100_000_000).optional(),
    // ── B3-2 (16-9): the thirteen remaining A2InputPPCHV2 fields ─────────
    /** Lage im Ort 1 (Ausserordentlich) … 5 (Schlecht), halve stappen — IAZI `qualityMicro`. */
    lageImOrt: z.number().min(1).max(5).multipleOf(0.5).optional(),
    /** Anzahl Badezimmer (IAZI `bathNb`; validator 1…15 — 0 = unbekannt, nicht senden). */
    badezimmer: z.number().int().min(0).max(50).optional(),
    /** Anzahl separate WC (IAZI `sepWcNb`). */
    separateWc: z.number().int().min(0).max(50).optional(),
    /** Separate Garagen (IAZI `parkCoverNb`; validator 0…100). */
    garagenparkplaetze: z.number().int().min(0).max(100).optional(),
    /** Tiefgaragenplätze (IAZI `parkOpenNb`). */
    tiefgaragenplaetze: z.number().int().min(0).max(100).optional(),
    /** Abstellplätze im Freien (IAZI `parkExternalNb`). */
    aussenparkplaetze: z.number().int().min(0).max(100).optional(),
    /** EFH: Dachstock ausbaubar (IAZI `roofFlag` — only the paid a2 input knows it). */
    dachstockAusbaubar: z.boolean().optional(),
    /** Ferienobjekt / Zweitwohnsitz (IAZI `primSecFlag`). */
    ferienobjekt: z.boolean().optional(),
    /** Minergie-Standard (IAZI `minergieCode` 0…6). */
    minergie: z.enum(MINERGIE_STUFEN).optional(),
    /** Wertrelevante Servitute (IAZI `servituteFlag`). */
    servitute: z.boolean().optional(),
    /** Baurecht: Verfalldatum ISO `YYYY-MM-DD`; leer = kein Baurecht (adapter: `buildRightDate 0`). */
    baurechtVerfall: z
        .string()
        .regex(BAURECHT_DATUM, 'baurechtVerfall: Datum als JJJJ-MM-TT')
        .refine(istKalenderdatum, 'baurechtVerfall: kein gültiges Datum')
        .optional(),
    /** Zählweise Zimmer (IAZI `roomShiftCode`; leer = Küche nicht gezählt, B3-1). */
    zaehlweiseZimmer: z.enum(ZAEHLWEISEN).optional(),
    /** EFH/EWG: Luxusobjekt Nein / Teilweise / Ja (IAZI `luxusFlag` 0 / 0.5 / 1). */
    luxusobjekt: z.enum(LUXUS_STUFEN).optional(),
    // ── Micro-6 (18-9): the three paid EWG fields the Web Tool has and we
    //    lacked — without them IAZI substituted all three and stamped every
    //    Wohnung «indikativ» (qualityCode 4). Scales measured in the Web Tool.
    /** EWG: Zustand der Wohnung — the condition words, half steps (IAZI `flatCondition` 1.0 … 4.0). */
    wohnungszustand: z.enum(ZUSTAND_STUFEN).optional(),
    /** EWG: Lage im Gebäude 1 Sehr gut … 4 Schlecht, half steps (IAZI `qualityFlatHouse`; Gut = 2). */
    lageImGebaeude: z.number().min(1).max(4).multipleOf(0.5).optional(),
    /** EWG: Wertquote in ‰ — IAZI `ownershipPercent`, «share of co-ownership (in per mill)»; 0 ‰ is no share (#587 MINOR-5). */
    wertquote: z.number().int().min(1).max(1000).optional(),
    // ── Order MFH-Rendite (23-9): the Renditeliegenschaft (MFH, a1 only) ──
    /** Wohnungsspiegel — see {@link wohnungsspiegelSchema}; `wohnungen`, if given, must equal its sum. */
    wohnungsspiegel: wohnungsspiegelSchema.optional(),
    /** Mietanteil Geschäft in %, one decimal (IAZI `rentRepartitionPercent`) — decides the subcategory Wohnen/Gemischt/Büro. */
    mietanteilGeschaeft: z
        .number()
        .min(0)
        .max(100)
        .refine(hoechstensEineDezimale, 'mietanteilGeschaeft: höchstens eine Nachkommastelle')
        .optional(),
    /** Bürofläche HNF2 (IAZI `surfaceOffice`). */
    bueroflaeche: nutzflaeche,
    /** Gewerbefläche HNF3 (IAZI `surfaceCommercial`). */
    gewerbeflaeche: nutzflaeche,
    /** Verkaufs-/Lagerfläche HNF4 (IAZI `surfaceRetail`). */
    verkaufsflaeche: nutzflaeche,
    /** Unterrichtsfläche HNF5 (IAZI `surfaceEducation`). */
    unterrichtsflaeche: nutzflaeche,
    /** Medizinische Fläche HNF6 (IAZI `surfaceMedical`). */
    medizinflaeche: nutzflaeche,
    /** Anzahl Aufzüge (IAZI `liftNb`, a count) — for an MFH it beats the Lift boolean. */
    aufzuege: z.number().int().min(0).max(50).optional(),
})
    .strict();
/**
 * IAZI-order 8-10 A1: which paid detail belongs to which Objekttyp — read from
 * the Maskenspezifikation (`iazi-maske.ts`), no second hand list: every
 * `paid` key of the mask (a Wohnungsspiegel box as `wohnungsspiegel`), the
 * second controls (`baurechtVerfall`) and the legacy `ausbaustandard`.
 */
function paidSchluessel(typ) {
    const keys = [];
    const dazu = (k) => {
        if (!keys.includes(k))
            keys.push(k);
    };
    for (const f of maskenFelder(typ)) {
        if (f.quelle !== 'paid')
            continue;
        dazu(f.key.split('.')[0]);
        if (f.zusatz)
            dazu(f.zusatz.key);
    }
    dazu('ausbaustandard');
    return keys;
}
export const PAID_MERKMALE_EFH = paidSchluessel('efh');
export const PAID_MERKMALE_EWG = paidSchluessel('ewg');
export const PAID_MERKMALE_MWG = paidSchluessel('mwg');
/** The per-Objekttyp assignment + the volumen<->volumenNorm pairing rule. */
function pruefePaidMerkmale(objektTyp, pm, ctx, path, baujahr) {
    if (!pm)
        return;
    // MINOR-4 (verdict #519): een renovatie VÓÓR het bouwjaar bestaat niet.
    // N3 (IAZI 30-9, prod 12:32): IN het bouwjaar evenmin — IAZI's validator
    // weigert «Letzte Sanierung nicht zwischen Baujahr und …» (modelfout 3, ná de
    // betaalde aanroep; Stockweg 6: 2024 = 2024). Dus `<=`, en hier, vóór de
    // aanroep. 0 = «Keine Renovation» is geen jaar en valt erbuiten.
    if (baujahr !== undefined &&
        pm.renovationsjahr !== undefined &&
        pm.renovationsjahr !== RENOVATION_KEINE &&
        pm.renovationsjahr <= baujahr) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...path, 'renovationsjahr'],
            message: RENOVATIONSJAHR_NACH_BAUJAHR,
        });
    }
    // … and not in the future either: IAZI's own validator bounds it by
    // «Baujahr … <current year + 2>» (measured 16-9, abname point 10) and would
    // otherwise replace it by «Keine» — silently. (0 is never above the bound.)
    if (pm.renovationsjahr !== undefined && pm.renovationsjahr > baujahrMax()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...path, 'renovationsjahr'],
            message: `renovationsjahr liegt nach ${baujahrMax()}`,
        });
    }
    const erlaubt = objektTyp === 'efh'
        ? PAID_MERKMALE_EFH
        : objektTyp === 'ewg'
            ? PAID_MERKMALE_EWG
            : PAID_MERKMALE_MWG;
    for (const k of Object.keys(pm)) {
        if (pm[k] !== undefined && !erlaubt.includes(k)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [...path, k],
                message: `${k} gilt nicht für objektTyp ${objektTyp}`,
            });
        }
    }
    // IAZI-order 8-10 A1: one quality scale per row — the legacy word OR the seven steps.
    if (pm.bauqualitaet !== undefined && pm.ausbaustandard !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...path, 'ausbaustandard'],
            message: 'bauqualitaet und ausbaustandard schliessen sich aus (Ausbaustandard ist Legacy)',
        });
    }
    // «Baurecht: Nein» with an expiry date says two things at once — refuse.
    if (pm.baurecht === false && pm.baurechtVerfall !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...path, 'baurechtVerfall'],
            message: 'baurechtVerfall nur mit baurecht: true',
        });
    }
    // volume without its norm (or vice versa) would make IAZI guess the standard
    // of a number we DID assert — refuse the half-pair instead.
    if ((pm.volumen === undefined) !== (pm.volumenNorm === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...path, pm.volumen === undefined ? 'volumen' : 'volumenNorm'],
            message: 'volumen und volumenNorm gehören zusammen (beide oder keins)',
        });
    }
    // Order MFH-Rendite: with a mix, IAZI's «Anzahl Wohnungen» IS its sum (the
    // Web App makes the field read-only). Two different numbers for the same
    // building would make the adapter pick one — refuse, naming both.
    if (pm.wohnungsspiegel !== undefined && pm.wohnungen !== undefined) {
        const summe = wohnungsspiegelSumme(pm.wohnungsspiegel);
        if (summe >= 1 && summe !== pm.wohnungen) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [...path, 'wohnungen'],
                message: `Anzahl Wohnungen ${pm.wohnungen} passt nicht zum Wohnungsspiegel (Summe ${summe})`,
            });
        }
    }
}
/**
 * Input an AvmProvider needs: the M0 object characteristics + the Gemeinde-level
 * location, plus the optional product tier and (paid-only) exact address.
 * Backward-compatible: the public funnel keeps sending just `{ object, location }`
 * and `product` defaults to `offered`; the CRM tool sends `product: 'paid'`
 * with an `address`. No invented object fields.
 */
export const avmInputSchema = z.object({
    object: valuationObjectSchema,
    location: valuationLocationSchema,
    /** Defaults to `offered` (public funnel) when omitted. */
    product: avmProductSchema.optional(),
    /** Exact address; meaningful only for `paid`, ignored otherwise. */
    address: avmAddressSchema.optional(),
    /** Internal ppch-only details (order B2); meaningful only for `paid`. */
    paidMerkmale: paidMerkmaleSchema.optional(),
    /** IAZI-order 8-10 A4: the ValuationRequest id as IAZI `externalKey` (paid). */
    externalKey: z.string().uuid().optional(),
});
/**
 * Create input for an INTERNAL (CRM) Immobilienbewertung — a broker running the
 * exact `paid` valuation. Distinct from the public `valuationIngestSchema`: no
 * contact/consent/turnstile (staff-only, server-guarded). The exact `address`
 * is optional (a Gemeinde-only estimate still works); `title`/`leadId` are CRM
 * bookkeeping. The server always calls the provider with `product: 'paid'`.
 */
/**
 * IAZI-order 8-10 A1/S2: the object of the paid DTO — `raeume` optional, because
 * the MFH mask has no «Zimmer» (the server derives it from the Wohnungsspiegel,
 * never sends it to cv). The rule lives on the DTO below: required, except for an
 * MFH whose mix has no Loft (a Loft has no room count — then the Makler gives it).
 */
export const immoObjektSchema = valuationObjectBaseSchema
    .extend({ raeume: z.number().positive().max(100).optional() })
    .superRefine(pruefeObjekt);
export const immoBewertungCreateSchema = z
    .object({
    object: immoObjektSchema,
    location: valuationLocationSchema,
    address: avmAddressSchema.optional(),
    /** Order B2: internal ppch-only details — see {@link paidMerkmaleSchema}. */
    paidMerkmale: paidMerkmaleSchema.optional(),
    title: z.string().trim().max(200).optional(),
    leadId: z.string().uuid().optional(),
})
    .superRefine((dto, ctx) => {
    // Same refusal rule as objektMerkmaleSchema's assignment: a floor on a
    // house or a rent on a flat is not harmless extra — refuse it.
    pruefePaidMerkmale(dto.object.objektTyp, dto.paidMerkmale, ctx, ['paidMerkmale'], dto.object.baujahr);
    const ws = dto.paidMerkmale?.wohnungsspiegel;
    const ohneZimmerErlaubt = dto.object.objektTyp === 'mwg' && !((ws?.loft ?? 0) > 0);
    if (dto.object.raeume === undefined && !ohneZimmerErlaubt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['object', 'raeume'],
            message: dto.object.objektTyp === 'mwg'
                ? 'raeume: mit Loft-Wohnungen das Zimmer-Total angeben (kein Vorschlag aus dem Wohnungsspiegel)'
                : 'raeume ist Pflicht',
        });
    }
});
/** Link an existing internal valuation to a Kontakt (lead). */
export const immoBewertungLinkSchema = z.object({ leadId: z.string().uuid() });
/**
 * «In Bewertung übernehmen» (owner 26-8, Fase A4): turn a funnel lead's
 * Bewertungsanfrage into a dossier (`origin='uebernommen'`). `bestaetigt` is
 * the confirmation the CRM dialog collects when the lead's Reifegrad is still
 * below `qualifiziert` — the server refuses (409) without it, so the dialog is
 * a contract and not a courtesy.
 */
export const immoBewertungUebernehmenSchema = z.object({
    leadId: z.string().uuid(),
    bestaetigt: z.boolean().default(false),
});
/**
 * Order 13-10 (owner): «Auswahl archivieren» in the CRM Verlauf — the ids of the
 * rows to archive (soft, reversible). One to a hundred per call, each a uuid,
 * none twice: a doubled id is a client bug, not a second archive, and is refused
 * (400) instead of silently folded. Review #665 F1: the Verlauf does NOT paginate
 * (every dossier on every page), so a selection can be larger than this — the
 * CRM sends it in chunks of at most this size (`archivierenValuations` in
 * `apps/crm/src/api/immobewertung.ts`).
 */
export const IMMO_BEWERTUNG_ARCHIVIEREN_MAX = 100;
export const immoBewertungArchivierenSchema = z.object({
    ids: z
        .array(z.string().uuid())
        .min(1)
        .max(IMMO_BEWERTUNG_ARCHIVIEREN_MAX)
        .refine((ids) => new Set(ids).size === ids.length, { message: 'ids_doppelt' }),
});
/**
 * Regional CHF/m² price point for the map layer (the AVM provider's area-level
 * product). Buy and rent are both optional — a provider may publish only one.
 * Gated behind `AVM_PRICE_LAYER_ENABLED` until the data licence is signed (and,
 * for BFS-sourced data, the commercial-use permission is in).
 */
export const regionalPriceSchema = z.object({
    gemeindeBfs: z.number().int().positive(),
    chfPerM2Buy: z.number().nonnegative().optional(),
    chfPerM2Rent: z.number().nonnegative().optional(),
    asOf: z.string().datetime(),
    provider: z.string().min(1),
});
